import { Box, FormLabel, Input, Textarea, useDisclosure } from '@chakra-ui/react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmationModal } from 'components/Modal';
import Table from 'components/Table';
import { useVisitsTableHandlers } from 'containers/Evv/shared/hooks/useVisitsTableHandlers';
import VisitsFilterGroup from 'containers/Evv/shared/VisitsFilterGroup';
import { IPendingVisitsSearchQuery, useFetchPaginatedPendingVisits } from 'hooks/evv/useFetchPaginatedPendingVisits';
import { IBulkMarkAsSynced, useBulkMarkAsSynced } from 'hooks/evv/useMarkAsSynced';
import { ISyncVisits, useSyncVisits } from 'hooks/evv/useSyncVisits';

import TableFooter from './TableFooter';
import { useTableColumns } from './useTableColumns';

const initialVisitsSearchQuery: IPendingVisitsSearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
  date_range: '',
  user_ids: '',
};

const Pending: React.FC<unknown> = () => {
  const notesRef = useRef<HTMLTextAreaElement | null>(null);
  const reasonCodeRef = useRef<HTMLInputElement | null>(null);
  const [dialogIds, setDialogIds] = useState<any>([]);

  const { t } = useTranslation(['evv', 'common']);
  const {
    isOpen: isSyncVisitModalOpen,
    onOpen: onSyncVisitModalOpen,
    onClose: onSyncVisitModalClose,
  } = useDisclosure();

  const {
    searchInput,
    searchQuery,
    handleDateChange,
    handlePageChange,
    handlePageSizeChange,
    handleSearchInputChange,
    handleEmployeeFilterFormSubmit,
  } = useVisitsTableHandlers({ initialSearchQuery: initialVisitsSearchQuery });

  const { status, data, refetch } = useFetchPaginatedPendingVisits(searchQuery);

  const { status: syncVisitsStatus, mutateAsync: mutateAsyncSyncVisits } = useSyncVisits();
  const { status: markAsSyncedStatus, mutateAsync: mutateAsyncMarkAsSynced } = useBulkMarkAsSynced();

  // deactivate user handler
  const handleSingleSynced = (id: number) => {
    setDialogIds([id]);
    onSyncVisitModalOpen();
  };

  const handleBulkSync = (selectedRows: any) => {
    const ids = selectedRows.map((row: any) => row.original.id);
    setDialogIds(ids);
    onSyncVisitModalOpen();
  };

  const syncVisits = () => {
    const postData: ISyncVisits = {
      timesheet_ids: dialogIds,
      notes: notesRef.current?.value || '',
      reason_code: reasonCodeRef.current?.value || '',
    };

    mutateAsyncSyncVisits(postData).then(() => {
      refetch();
      onSyncVisitModalClose();
      setDialogIds([]);
    });
  };

  // Mark synced timesheet handler
  const handleBulkMarkSynced = (selectedRows: any) => {
    const ids = selectedRows.map((row: any) => row.original.id);
    const postData: IBulkMarkAsSynced = { timesheet_ids: ids };
    mutateAsyncMarkAsSynced(postData).then(() => {
      refetch();
    });
  };

  const { columns } = useTableColumns({ handleSingleSynced });

  return (
    <div>
      <ConfirmationModal
        isOpen={isSyncVisitModalOpen}
        onClose={onSyncVisitModalClose}
        title={t('evv:submitVisitModalTitle')}
        subTitle={t('evv:submitVisitModalSubTitle')}
        body={
          <Box mt="4" px="4">
            <FormLabel
              htmlFor="notes"
              color="gray.500"
              fontSize="12px"
              fontWeight="600"
              mb="1"
              textTransform="uppercase"
            >
              Visit/Change explanation:
            </FormLabel>{' '}
            <Textarea name="notes" ref={notesRef} />
            <FormLabel
              htmlFor="reasonCode"
              color="gray.500"
              fontSize="12px"
              fontWeight="600"
              mb="1"
              textTransform="uppercase"
              mt="4"
            >
              Reason Code:
            </FormLabel>{' '}
            <Input name="reasonCode" ref={reasonCodeRef} />
          </Box>
        }
        confirmButtonLabel={t('common:submit')}
        confirmButtonProps={{
          bg: 'rgba(72,82, 175, 0.1)',
          color: 'primary.500',
          _hover: {
            bg: 'rgba(72,82, 175, 0.2)',
          },
          _active: {
            bg: 'rgba(72,82, 175, 0.2)',
          },
          isLoading: syncVisitsStatus === 'loading',
          onClick: syncVisits,
        }}
      />

      <VisitsFilterGroup
        handleEmployeeFilterFormSubmit={handleEmployeeFilterFormSubmit}
        handleSearchInputChange={handleSearchInputChange}
        handleDateChange={handleDateChange}
        searchQuery={searchQuery}
        searchInput={searchInput}
      />

      <Table
        columns={columns}
        data={data?.payload || []}
        paginationData={data?.meta_data?.pagination as any}
        isLoading={status === 'loading'}
        enableRowSelect
        onPageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
        paginationStyle="rowsPerPage"
        renderTableFooter={(selectedFlatRows: any, toggleAllRowSelected: any) => {
          return (
            <TableFooter
              selectedFlatRows={selectedFlatRows}
              toggleAllRowSelected={toggleAllRowSelected}
              handleBulkSync={handleBulkSync}
              handleBulkMarkSynced={handleBulkMarkSynced}
              markAsSyncedStatus={markAsSyncedStatus}
            />
          );
        }}
      />
    </div>
  );
};

export default Pending;
